import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Loader2, Search, X } from 'lucide-react';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useApi } from '@/hooks/useApi';
import { useAdminTeams } from '@/hooks/useAdmin';
import { teamLabel } from '@/lib/labels';
import { useQuery } from '@tanstack/react-query';
import type { OutreachInviter } from '@/api/types';

/**
 * Lets admins toggle between the Admin and Outreacher surfaces.
 * When switching to Outreacher view, a picker lets them choose which
 * outreacher to impersonate. Hidden for non-admin users.
 */
export function ViewSwitcher(): JSX.Element | null {
  const isAdmin = useIsAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { inviterId, inviterName, startImpersonating, stopImpersonating } = useImpersonation();
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!isAdmin) return null;

  const isOnPortal = location.pathname.startsWith('/portal');

  function handleSwitchToAdmin() {
    stopImpersonating();
    queryClient.invalidateQueries();
    navigate('/admin');
  }

  if (isOnPortal) {
    return (
      <div className="flex items-center gap-1.5">
        {inviterName && (
          <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">
            Viewing as {inviterName}
          </span>
        )}
        <button
          onClick={handleSwitchToAdmin}
          className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          Admin view
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setPickerOpen(true)}
        className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <ArrowLeftRight className="h-3.5 w-3.5" />
        Outreacher view
      </button>
      {pickerOpen && (
        <InviterPicker
          onSelect={(inviter) => {
            startImpersonating(inviter.id, inviter.name);
            setPickerOpen(false);
            queryClient.invalidateQueries();
            navigate('/portal');
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}

function InviterPicker({
  onSelect,
  onClose,
}: {
  onSelect: (inviter: OutreachInviter) => void;
  onClose: () => void;
}): JSX.Element {
  const api = useApi();
  const [search, setSearch] = useState('');
  const teams = useAdminTeams();
  const teamName = (code: string): string =>
    teamLabel(code, teams.data?.find((t) => t.code === code)?.displayName);
  const inviters = useQuery({
    queryKey: ['admin-inviters-picker', search],
    queryFn: () =>
      api.get<OutreachInviter[]>('/admin/inviters', {
        isActive: true,
        limit: 20,
        ...(search ? { search } : {}),
      }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-popover shadow-overlay">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium">Select an outreacher to view as</h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-border px-4 py-2">
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-2">
          {inviters.isLoading && (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {inviters.data?.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No outreachers found</p>
          )}
          {inviters.data?.map((inviter) => (
            <button
              key={inviter.id}
              onClick={() => onSelect(inviter)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              <div className="flex-1">
                <div className="font-medium">{inviter.name}</div>
                <div className="text-xs text-muted-foreground">
                  {teamName(inviter.team)} · {inviter.email ?? 'no email'}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
